/**
 * Routing and the session gate.
 *
 * Client-side route protection is convenience, never enforcement — anyone can
 * edit their own JavaScript. Every API route re-checks the session server-side
 * and resolves its own tenant scope. This only decides what to render.
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useSession } from './lib/auth.ts';
import Login from './pages/Login.tsx';
import Portal, { PortalUnavailable } from './pages/Portal.tsx';
import SignInConfirm from './pages/SignInConfirm.tsx';
import Home from './pages/Home.tsx';
import SettingsPage from './pages/Settings.tsx';
import TextSettings from './pages/TextSettings.tsx';
import UsersPage from './pages/Users.tsx';
import AppShell from './components/AppShell.tsx';
import PortalPreview from './pages/PortalPreview.tsx';
import Workspace from './pages/Workspace.tsx';

function RequireSession({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();

  // Render nothing rather than a flash of the login page while the session
  // request is in flight.
  if (isPending) return <div className="app-loading" aria-busy="true" />;
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function LoginRoute() {
  const { data: session, isPending } = useSession();
  if (isPending) return <div className="app-loading" aria-busy="true" />;
  if (session) return <Navigate to="/" replace />;
  return <Login />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        {/* Reachable without a session by definition — it is how you get one. */}
        <Route path="/signin/confirm" element={<SignInConfirm />} />

        {/* Sponsor portal. Outside RequireSession entirely: a sponsor has no
            account, and their access comes from the cookie the /s/:token
            exchange sets server-side before this route is ever reached. */}
        <Route path="/portal" element={<Portal />} />
        <Route path="/portal/unavailable" element={<PortalUnavailable />} />
        {/* The sponsor portal as staff, on its own route and OUTSIDE AppShell.
            The shell is the point: a preview rendered inside the admin sidebar,
            at whatever width it leaves over, is a picture of the page rather
            than the page. Still behind RequireSession, because it reads one
            sponsor's real submissions. */}
        <Route
          path="/c/:clientId/events/:eventId/portal-preview"
          element={
            <RequireSession>
              <PortalPreview />
            </RequireSession>
          }
        />

        <Route
          path="/"
          element={
            <RequireSession>
              <Home />
            </RequireSession>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireSession>
              <AppShell active="settings" context="Front Porch Solutions · platform settings">
                <SettingsPage />
              </AppShell>
            </RequireSession>
          }
        />
        {/* The text catalogue. Its own route rather than a tab inside Settings:
            it is a working surface somebody spends an hour in, not one more
            panel of switches. */}
        <Route
          path="/settings/text"
          element={
            <RequireSession>
              <AppShell active="settings" context="Front Porch Solutions · application text">
                <TextSettings />
              </AppShell>
            </RequireSession>
          }
        />
        <Route
          path="/c/:clientId"
          element={
            <RequireSession>
              <Workspace view="dashboard" />
            </RequireSession>
          }
        />
        <Route
          path="/c/:clientId/events"
          element={
            <RequireSession>
              <Workspace view="events" />
            </RequireSession>
          }
        />
        <Route
          path="/c/:clientId/events/:eventId"
          element={
            <RequireSession>
              <Workspace view="event" />
            </RequireSession>
          }
        />
        <Route
          path="/c/:clientId/events/:eventId/sponsors/:eventSponsorId"
          element={
            <RequireSession>
              <Workspace view="sponsor" />
            </RequireSession>
          }
        />
        <Route
          path="/users"
          element={
            <RequireSession>
              <AppShell active="users" context="Front Porch Solutions · platform access">
                <UsersPage />
              </AppShell>
            </RequireSession>
          }
        />
        <Route
          path="/c/:clientId/library"
          element={
            <RequireSession>
              <Workspace view="library" />
            </RequireSession>
          }
        />
        <Route
          path="/c/:clientId/library/:sponsorId"
          element={
            <RequireSession>
              <Workspace view="library-detail" />
            </RequireSession>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
